import { useEffect, useMemo, useState } from "react";
import { Camera, CheckCircle2, Cpu, Gauge, MapPin, PhoneCall, Send, Settings, Truck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OPERATIONAL_ROTATION_SLOT_MAP } from "./config";
import { EditableText } from "./TextEditor";

const CapabilityBadge = ({ children, testId }) => <span data-testid={testId}>{children}</span>;

export const buildSectionImages = (imageSlots, slotKeys) => [...new Set(slotKeys.map((key) => imageSlots[key]).filter(Boolean))];

export const useRotatingImages = (images) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % images.length);
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [images]);

  const normalizedIndex = images.length ? activeIndex % images.length : 0;

  return {
    activeImage: images[normalizedIndex] || "",
    activeIndex: normalizedIndex,
  };
};

export const RotationDots = ({ testIdPrefix, images, activeIndex }) => (
  images.length > 1 ? (
    <div data-testid={`${testIdPrefix}-rotation-dots`} className="operational-rotation-dots">
      {images.map((image, index) => (
        <span
          data-testid={`${testIdPrefix}-rotation-dot-${index}`}
          key={`${testIdPrefix}-${image}`}
          className={index === activeIndex ? "is-active" : ""}
        />
      ))}
    </div>
  ) : null
);

export const HeavyRecoverySection = ({ imageSlots }) => {
  const sectionImages = useMemo(() => buildSectionImages(imageSlots, OPERATIONAL_ROTATION_SLOT_MAP.heavy), [imageSlots]);
  const { activeImage, activeIndex } = useRotatingImages(sectionImages);

  return (
    <section id="mezzi-pesanti" data-testid="heavy-recovery-section" className="section split-section">
      <div className="container split-grid">
        <div data-testid="heavy-recovery-image" className="image-panel image-panel--heavy operational-photo-frame">
          {activeImage ? (
            <img data-testid="heavy-recovery-active-image" src={activeImage} alt="Soccorso mezzi pesanti e autocarri" />
          ) : (
            <div data-testid="heavy-recovery-image-placeholder" className="empty-photo-placeholder">&nbsp;</div>
          )}
          <div className="image-panel__badge">Soccorso pesante</div>
          <RotationDots testIdPrefix="heavy-recovery" images={sectionImages} activeIndex={activeIndex} />
        </div>
        <div data-testid="heavy-recovery-content" className="split-content">
          <EditableText textId="heavy.section.eyebrow" defaultText="Soccorso pesante" as="span" className="section-eyebrow" />
          <EditableText textId="heavy.section.title" defaultText="Centrale heavy recovery per camion, autobus e semirimorchi" as="h2" />
          <EditableText textId="heavy.section.description" defaultText="Viscar coordina soccorso pesante H24 per camion, autobus, motrici e semirimorchi con base a Messina e copertura su A18, A20, svincolo di Tremestieri e principali collegamenti." as="p" multiline />
          <div data-testid="heavy-recovery-badges" className="badge-row badge-row--large">
            {['Heavy Recovery', 'Truck Support', 'Mezzi pesanti', 'A18 / A20', 'Tremestieri', 'Centrale H24'].map((item, index) => <CapabilityBadge key={item} testId={`capability-heavy-${index}`}><EditableText textId={`heavy.section.badge.${index}`} defaultText={item} as="span" mode="click" /></CapabilityBadge>)}
          </div>
        </div>
      </div>
    </section>
  );
};

export const LightDutySection = ({ imageSlots }) => {
  const sectionImages = useMemo(() => buildSectionImages(imageSlots, OPERATIONAL_ROTATION_SLOT_MAP.lightDuty), [imageSlots]);
  const { activeImage, activeIndex } = useRotatingImages(sectionImages);

  return (
    <section id="soccorso-leggero" data-testid="light-duty-section" className="section split-section split-section--light-duty">
      <div className="container split-grid split-grid--reverse">
        <div data-testid="light-duty-content" className="split-content">
          <EditableText textId="light.section.eyebrow" defaultText="Soccorso leggero" as="span" className="section-eyebrow" />
          <EditableText textId="light.section.title" defaultText="Soccorso leggero per auto, SUV, furgoni e veicoli commerciali" as="h2" />
          <EditableText textId="light.section.description" defaultText="Intervento rapido per auto, SUV, furgoni e veicoli commerciali con uscita dalla centrale operativa di Messina verso città, viabilità ordinaria e tratte autostradali principali." as="p" multiline />
          <div data-testid="light-duty-badges" className="badge-row badge-row--large">
            {["Light Duty", "Road Assistance", "Van Support", "Messina", "A18 / A20", "Partenza rapida"].map((item, index) => <CapabilityBadge key={item} testId={`capability-light-${index}`}><EditableText textId={`light.section.badge.${index}`} defaultText={item} as="span" mode="click" /></CapabilityBadge>)}
          </div>
        </div>
        <div data-testid="light-duty-image" className="image-panel operational-photo-frame">
          {activeImage ? (
            <img data-testid="light-duty-active-image" src={activeImage} alt="Soccorso leggero per auto, SUV e furgoni" />
          ) : (
            <div data-testid="light-duty-image-placeholder" className="empty-photo-placeholder">&nbsp;</div>
          )}
          <div className="image-panel__badge">Light duty</div>
          <RotationDots testIdPrefix="light-duty" images={sectionImages} activeIndex={activeIndex} />
        </div>
      </div>
    </section>
  );
};

export const MobileWorkshopSection = ({ imageSlots }) => {
  const sectionImages = useMemo(() => buildSectionImages(imageSlots, OPERATIONAL_ROTATION_SLOT_MAP.mobileWorkshop), [imageSlots]);
  const { activeImage, activeIndex } = useRotatingImages(sectionImages);

  return (
    <section id="officina-mobile" data-testid="mobile-workshop-section" className="section workshop-section">
      <div className="container workshop-grid">
        <div data-testid="mobile-workshop-content" className="split-content">
          <EditableText textId="mobile.section.eyebrow" defaultText="Officina mobile" as="span" className="section-eyebrow" />
          <EditableText textId="mobile.section.title" defaultText="Officina mobile, truck support e cambio gomme autocarro" as="h2" />
          <EditableText textId="mobile.section.description" defaultText="Unità mobile per ripristini tecnici su strada, cambio gomme autocarro e supporto operativo quando il mezzo può essere rimesso in marcia sul posto." as="p" multiline />
          <div data-testid="mobile-workshop-badges" className="badge-row badge-row--large">
            {['Mobile Unit', 'Truck Support', 'Road Service', 'Ripartenza mezzo'].map((item, index) => <CapabilityBadge key={item} testId={`capability-mobile-${index}`}><EditableText textId={`mobile.section.badge.${index}`} defaultText={item} as="span" mode="click" /></CapabilityBadge>)}
          </div>
        </div>
        <div data-testid="mobile-workshop-modules" className="workshop-modules operational-photo-frame">
          {activeImage ? (
            <img data-testid="mobile-workshop-image" src={activeImage} alt="Officina mobile cambio gomme autocarro" />
          ) : (
            <div data-testid="mobile-workshop-image-placeholder" className="empty-photo-placeholder empty-photo-placeholder--wide">&nbsp;</div>
          )}
          <RotationDots testIdPrefix="mobile-workshop" images={sectionImages} activeIndex={activeIndex} />
        </div>
      </div>
    </section>
  );
};

export const ComplexRecoverySection = ({ imageSlots }) => {
  const sectionImages = useMemo(() => buildSectionImages(imageSlots, OPERATIONAL_ROTATION_SLOT_MAP.complex), [imageSlots]);
  const { activeImage, activeIndex } = useRotatingImages(sectionImages);

  return (
    <section id="recuperi-complessi" data-testid="complex-recovery-section" className="section split-section split-section--complex">
      <div className="container split-grid">
        <div data-testid="complex-recovery-image" className="image-panel operational-photo-frame">
          {activeImage ? (
            <img data-testid="complex-recovery-active-image" src={activeImage} alt="Recuperi complessi, incidenti e veicoli bloccati" />
          ) : (
            <div data-testid="complex-recovery-image-placeholder" className="empty-photo-placeholder">&nbsp;</div>
          )}
          <div className="image-panel__badge">Recovery team</div>
          <RotationDots testIdPrefix="complex-recovery" images={sectionImages} activeIndex={activeIndex} />
        </div>
        <div data-testid="complex-recovery-content" className="split-content">
          <EditableText textId="complex.section.eyebrow" defaultText="Recuperi complessi" as="span" className="section-eyebrow" />
          <EditableText textId="complex.section.title" defaultText="Recuperi complessi, incidenti e mezzi bloccati" as="h2" />
          <EditableText textId="complex.section.description" defaultText="Gestione H24 di incidenti, ribaltamenti e mezzi bloccati con mezzi attrezzati, supporto gru e coordinamento operativo diretto dalla centrale Viscar." as="p" multiline />
          <div data-testid="complex-recovery-badges" className="badge-row badge-row--large">
            {['Recovery Team', 'Crane Support', 'Heavy Duty', 'Incidenti', 'Centrale H24'].map((item, index) => <CapabilityBadge key={item} testId={`capability-complex-${index}`}><EditableText textId={`complex.section.badge.${index}`} defaultText={item} as="span" mode="click" /></CapabilityBadge>)}
          </div>
        </div>
      </div>
    </section>
  );
};

export const ProcessSection = () => {
  const steps = [
    { icon: PhoneCall, title: "Chiama o scrivi", text: "Contatto immediato con telefono o WhatsApp." },
    { icon: MapPin, title: "Invia posizione", text: "Comunica dove si trova il mezzo e il problema." },
    { icon: Send, title: "Partenza mezzo", text: "Viscar invia il mezzo più adatto all’intervento." },
  ];

  return (
    <section data-testid="process-section" className="section process-section">
      <div className="container">
        <div data-testid="process-header" className="section-header">
          <EditableText textId="process.header.eyebrow" defaultText="Attivazione centrale" as="span" className="section-eyebrow" />
          <EditableText textId="process.header.title" defaultText="Come attivare il soccorso senza perdere tempo" as="h2" />
          <EditableText textId="process.header.description" defaultText="Tre passaggi rapidi per far partire il mezzo giusto dalla centrale operativa Viscar." as="p" multiline />
        </div>
        <div data-testid="process-steps" className="process-grid">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <article data-testid={`process-step-${index}`} key={step.title} className="process-card">
                <EditableText textId={`process.step.${index}.badge`} defaultText="SOS" as="span" testId={`process-step-${index}-number`} className="process-number" mode="click" />
                <Icon size={25} />
                <EditableText textId={`process.step.${index}.title`} defaultText={step.title} as="h3" />
                <EditableText textId={`process.step.${index}.text`} defaultText={step.text} as="p" multiline />
              </article>
            );
          })}
        </div>
        <a data-testid="process-photo-note" className="photo-note" href="#richiesta"><Camera size={18} /> <EditableText textId="process.photo.note" defaultText="Puoi indicare note e inviare immagini direttamente in chat WhatsApp." as="span" mode="click" /></a>
      </div>
    </section>
  );
};

export const TechnicalAssistanceSection = () => {
  const technicalItems = [
    { icon: Settings, title: "Officina meccanica", text: "Supporto tecnico e interventi di ripristino per veicoli e mezzi commerciali." },
    { icon: Gauge, title: "Servizio gommista", text: "Assistenza pneumatici, cambio gomme autocarro e supporto su strada." },
    { icon: Cpu, title: "Diagnosi elettronica", text: "Verifiche tecniche e diagnosi per individuare rapidamente anomalie del mezzo." },
    { icon: Truck, title: "Supporto mezzi commerciali", text: "Assistenza per auto, furgoni, autocarri e veicoli da lavoro." },
  ];

  return (
    <section data-testid="technical-assistance-section" className="section technical-assistance-section">
      <div className="container technical-assistance-panel">
        <div data-testid="technical-assistance-content" className="technical-assistance-copy">
          <EditableText textId="technical.header.eyebrow" defaultText="Struttura tecnica interna" as="span" className="section-eyebrow" />
          <EditableText textId="technical.header.title" defaultText="Alla centrale H24 si affianca una struttura tecnica interna" as="h2" />
          <EditableText textId="technical.header.description" defaultText="Oltre al soccorso stradale, Viscar dispone di officina meccanica, supporto pneumatici, diagnosi elettronica e assistenza tecnica per auto, furgoni, autocarri e mezzi commerciali." as="p" multiline />
        </div>
        <div data-testid="technical-assistance-grid" className="technical-assistance-grid">
          {technicalItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <article data-testid={`technical-assistance-card-${item.title.toLowerCase().replaceAll(" ", "-")}`} key={item.title} className="technical-assistance-card">
                <Icon size={22} />
                <EditableText textId={`technical.card.${index}.title`} defaultText={item.title} as="h3" />
                <EditableText textId={`technical.card.${index}.text`} defaultText={item.text} as="p" multiline />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};